import React, { Suspense, lazy } from 'react'
import Immutable from 'immutable'
import { State, hasLoadedWithoutError } from '@rwwa/rwwa-data-access'
import {
  GiddyUpAcceptors,
  IAcceptor,
  IAcceptorRecord,
} from '@core/Areas/Racing/Components/RacingForm/data-access/giddyUpAcceptors'
import {
  GiddyUpRaces,
  IRace,
  IRaceRecord,
  MeetingCode,
} from '@core/Areas/Racing/Components/RacingForm/data-access/giddyUpRaces'
import { trackGiddyUpShowSpeedMap } from '@core/Areas/Racing/Components/RacingForm/helpers/analytics'
import { observe } from '@core/Components/HOCs/observe'
import { SpeedMapWrapperStyled } from '../Common/GiddyUp.styles'
import { chevronPath, leftArrowPath, rightArrowPath } from '../Common/Paths/raw'
import { SpeedMapAcceptorList } from './Components/SpeedMapAcceptorList'

const HorsePath = lazy(() => import('../Common/Paths/Lazy/HorsePath'))
const TrotPath = lazy(() => import('../Common/Paths/Lazy/TrotPath'))

const enum LocalConstants {
  LaneHeight = 75,
  ViewWidth = 740,
}

interface IComponentState {
  raceState: State<IRaceRecord>
  acceptorState: State<IAcceptorRecord>
}

type ViewPosition = 'Leading' | 'Following'
const SpeedMapContext = React.createContext<{
  viewPosition: ViewPosition
  setViewPosition(position: ViewPosition): void
}>({
  viewPosition: 'Following',
  setViewPosition: () => null,
})

const SpeedMapComponent: React.FC<IComponentState & { raceKey: string }> = ({
  raceState,
  acceptorState,
  raceKey,
}) => {
  const [viewPositionContextValue, setViewPositionContextValue] =
    React.useState<ViewPosition>('Following')

  const race = GiddyUpRaces.getFrom(raceState, raceKey)

  React.useEffect(trackGiddyUpShowSpeedMap, [])

  if (!hasLoadedWithoutError(race)) {
    return <div />
  }

  const acceptors = race.sortedAcceptorKeys
    .map(starterKey => starterKey && GiddyUpAcceptors.getFrom(acceptorState, starterKey))
    .filter(a => !!a && hasLoadedWithoutError(a)) as Immutable.Iterable<number, IAcceptorRecord>

  const mappedAcceptors = getAcceptors(race, acceptors)

  return (
    <SpeedMapContext.Provider
      value={{
        viewPosition: viewPositionContextValue,
        setViewPosition: setViewPositionContextValue,
      }}
    >
      <SpeedMapWrapperStyled data-tid-speed-map=''>
        <VisualMap
          acceptors={mappedAcceptors}
          meetingCode={race.meetingCode}
          isMovedLeft={viewPositionContextValue === 'Leading'}
        />
        <SpeedMapAcceptorList acceptors={acceptors} />
      </SpeedMapWrapperStyled>
    </SpeedMapContext.Provider>
  )
}

const combinedState$ = Rx.Observable.combineLatest(
  GiddyUpRaces.data$,
  GiddyUpAcceptors.data$,
  (raceState, acceptorState) => ({ raceState, acceptorState })
)

export const SpeedMap = observe<IComponentState, { raceKey: string }>(
  combinedState$,
  SpeedMapComponent
)

// ================
// Local Components
// ================

const VisualMap: React.FC<{
  isMovedLeft: boolean
  acceptors: Immutable.Iterable<number, IPosition>
  meetingCode: MeetingCode
}> = ({ isMovedLeft, acceptors, meetingCode }) => {
  const numberOfMinLanes = meetingCode === MeetingCode.Races ? 3 : 2
  const numberOfLanes = Math.max(
    numberOfMinLanes,
    acceptors.map((a: IPosition) => a.lane).max() || 0
  )
  const showMoveButton = Math.ceil(acceptors.map((a: IPosition) => a.offPace).max()) >= 7
  const railHeight = 110
  const heightOffset = 100
  const viewX = isMovedLeft ? -1420 : -720

  const lengths = Array(15)
    .fill(null)
    .map((x, i) => (i === 0 ? 'Lead' : `${i}L`))

  const glpyh = meetingCode === MeetingCode.Races ? AcceptorGlyphRaces : AcceptorGlyphTrots

  const viewHeight =
    railHeight + numberOfLanes * LocalConstants.LaneHeight + (showMoveButton ? heightOffset : 0)
  return (
    <div className={meetingCode === MeetingCode.Races ? 'thoroughbredsMap' : 'trotsMap'}>
      <Suspense fallback={null}>
        <svg
          data-tid-visualmap
          viewBox={`${viewX}, 0, ${LocalConstants.ViewWidth}, ${viewHeight}`}
          className='visualMap'
        >
          <g transform='translate(0)'>
            <Track
              lengths={lengths}
              lanes={numberOfLanes}
              showMoveButton={showMoveButton}
              isMovedLeft={isMovedLeft}
              viewX={viewX}
            />
            {/* @ts-expect-error typing issue */}
            <Field glyph={glpyh} acceptors={acceptors} />
          </g>
        </svg>
      </Suspense>
    </div>
  )
}

const Track: React.FC<{
  lengths: string[]
  lanes: number
  showMoveButton: boolean
  viewX: number
  isMovedLeft: boolean
}> = ({ lengths, lanes, showMoveButton, viewX, isMovedLeft }) => {
  const railWidth = '4'

  return (
    <g transform='translate(0, 0)'>
      {Array(Math.ceil(lanes / 2))
        .fill(0)
        .map((l, i) => {
          const laneStart = LocalConstants.LaneHeight * 2 * (i + 1)
          return (
            <rect
              className='track'
              key={i}
              x={viewX}
              y={laneStart}
              height={LocalConstants.LaneHeight}
              width={LocalConstants.ViewWidth}
            />
          )
        })}

      <line className='rail' x1={viewX} y1='39' x2='20' y2='39' strokeWidth={railWidth} />

      {lengths.map((lengthName, i) => {
        const nameLeft = 5 - i * 100
        const nameTop = 25
        const postLeft = nameLeft - 10
        const postTop = 40
        return (
          <g key={lengthName}>
            <text
              className='lengthName'
              transform={`translate(${nameLeft},${nameTop})`}
              textAnchor='end'
              fontSize='20'
            >
              {lengthName}
            </text>

            <path
              className='rail'
              transform={`translate(${postLeft},${postTop})`}
              d='M0,0l5,5l0,35'
              fill='none'
              strokeWidth={railWidth}
            />
          </g>
        )
      })}

      <line className='rail' x1='0' y1='80' x2='0' y2='600' strokeWidth='3' />

      {showMoveButton ? (
        <MoveViewButtons lanes={lanes} viewX={viewX} isMovedLeft={isMovedLeft} />
      ) : null}
    </g>
  )
}

const Field: React.FC<{
  acceptors: Immutable.Iterable<number, IPosition>
  glyph: (key: number, lane: number, off: number, acceptorNumber: number) => JSX.Element
}> = ({ acceptors, glyph }) => {
  const Glyph = glyph
  const glyhps = acceptors
    // @ts-expect-error typing issue to tack later
    .sortBy((acceptor: IPosition) => -acceptor.offPace)
    // @ts-expect-error typing issue to tack later
    .sortBy((acceptor: IPosition) => acceptor.lane)
    .map((acceptor: IPosition) => (
      // @ts-expect-error typing issue to done later
      <Glyph
        key={acceptor.acceptorNumber}
        lane={acceptor.lane}
        off={acceptor.offPace}
        acceptorNumber={acceptor.acceptorNumber}
      />
    ))
    .valueSeq()
    .toJS()
  return <g transform='translate(0, 55)'> {glyhps} </g>
}

const AcceptorGlyphRaces: React.FC<{
  lane: number
  off: number
  acceptorNumber: number
}> = ({ lane, off, acceptorNumber }) => {
  const right = off * 100
  const top = LocalConstants.LaneHeight * (lane - 1)

  return (
    <g data-tid-acceptor-glyph={acceptorNumber} transform={`translate(${-right},${top})`}>
      <g transform='translate(-100)'>
        <ellipse className='shadow' cx='30' cy='110' rx='70' ry='8' stroke='none' />

        <HorsePath fill='none' className='silhouette' strokeWidth='5' />

        <HorsePath fillRule='evenodd' />

        <path fillRule='evenodd' fill='#FFF' stroke='#000' strokeMiterlimit='10' d={chevronPath} />

        <text transform='matrix(1 0 0 1 31 65)' textAnchor='middle' fontSize='20'>
          {acceptorNumber}
        </text>
      </g>
    </g>
  )
}

const AcceptorGlyphTrots: React.FC<{
  lane: number
  off: number
  acceptorNumber: number
}> = ({ lane, off, acceptorNumber }) => {
  const right = off * 100
  const top = LocalConstants.LaneHeight * (lane - 1)
  return (
    <g data-tid-acceptor-glyph={acceptorNumber} transform={`translate(${-right},${top})`}>
      <g transform='translate(-100)'>
        <ellipse className='shadow' cx='30' cy='110' rx='70' ry='8' stroke='none' />

        <TrotPath fill='none' className='silhouette' strokeWidth='5' />

        <TrotPath fillRule='evenodd' fill='#542C1D' />

        <g transform='translate(-25)'>
          <path
            fillRule='evenodd'
            fill='#FFF'
            stroke='#000'
            strokeMiterlimit='10'
            d={chevronPath}
          />

          <text transform='translate(31 65)' textAnchor='middle' fontSize='20'>
            {acceptorNumber}
          </text>
        </g>
      </g>
    </g>
  )
}

const MoveViewButtons: React.FC<{
  lanes: number
  viewX: number
  isMovedLeft: boolean
}> = ({ lanes, viewX, isMovedLeft }) => {
  const viewPositionContext = React.useContext(SpeedMapContext)
  const buttonY = 200 + LocalConstants.LaneHeight * (lanes - 1)
  const moveLeftClick = () => viewPositionContext.setViewPosition('Leading')
  const moveRightClick = () => viewPositionContext.setViewPosition('Following')

  return (
    <g
      data-tid-movebutton=''
      className='followingText'
      transform={`translate(${viewX + 25}, ${buttonY})`}
      onClick={isMovedLeft ? moveRightClick : moveLeftClick}
    >
      <rect
        x='0'
        y='0'
        width={LocalConstants.ViewWidth - 50}
        height={LocalConstants.LaneHeight}
        fill='#fff'
      />

      <g
        transform={`translate(${(LocalConstants.ViewWidth - 50) / 2}, ${LocalConstants.LaneHeight / 2 + 10})`}
      >
        <text transform='translate(0 0)' textAnchor='middle'>
          {isMovedLeft ? 'Leading' : 'Following'}
        </text>

        {isMovedLeft ? (
          <svg x='50' y='-37' width='20' height='40' viewBox='0 0 500 100'>
            <path d={rightArrowPath} />
          </svg>
        ) : (
          <svg x='-80' y='-37' width='20' height='40' viewBox='0 0 500 100'>
            <path d={leftArrowPath} />
          </svg>
        )}
      </g>
    </g>
  )
}

// =============
// Local Helpers
// =============

function getAcceptors(
  race: IRace,
  acceptors: Immutable.Iterable<number, IAcceptor | null>
): Immutable.Iterable<number, IPosition> {
  const filterAcceptors = acceptors.filter(a => !!a && !a.isGiddyupScratched) as Immutable.Iterable<
    number,
    IAcceptor
  >
  switch (race.meetingCode) {
    case MeetingCode.Races:
      return getThoroughbredAcceptors(filterAcceptors)

    case MeetingCode.Trots:
      return getTrotAcceptors(filterAcceptors)
    default:
      return Immutable.Map<number, IPosition>()
  }
}

function getThoroughbredAcceptors(acceptors: Immutable.Iterable<number, IAcceptor>) {
  const minSpeedmap = acceptors.map((a: IAcceptor) => a.speedMap).min()
  return acceptors.map((a: IAcceptor) => ({
    lane: a.speedMapLane,
    offPace: a.speedMap - minSpeedmap,
    acceptorNumber: a.acceptorNumber,
  }))
}

function getTrotAcceptors(acceptors: Immutable.Iterable<number, IAcceptor>) {
  const minSpeedmap = acceptors.map((a: IAcceptor) => a.speedMap).min()
  return acceptors.map((a: IAcceptor) => ({
    lane: a.speedMapLane === 3 ? 2 : a.speedMapLane,
    offPace: a.speedMap - minSpeedmap,
    acceptorNumber: a.acceptorNumber,
  }))
}

// =====
// Types
// =====

interface IPosition {
  lane: number
  offPace: number
  acceptorNumber: number
}
